import { useEffect, useRef, useState } from 'react';
import fechasBanner from '../../../../assets/fechas.webp';
import { GAME_LOCATIONS } from '../../data/gameLocations';
import { renderQrToCanvas } from '../../infrastructure/qrCode';
import { resolveTournamentShareText } from '../../infrastructure/tournamentLinks';
import { Panel, replayButton, secondary } from './ExperienceLayout';

const bannerAlt = 'Ñaca Ñaca 3ra Edición — Festival Internacional de Cine de Terror, del 02 al 04 de octubre';
const shareButton = 'min-h-[44px] border border-[#7EC54A]/60 bg-[#1A4D2E]/80 px-2 py-3 text-[10px] font-black uppercase tracking-wider text-white';

function FestivalBanner() {
  return <img src={fechasBanner} alt={bannerAlt} className="mx-auto h-auto w-[min(64vw,200px)] object-contain" />;
}

function TournamentSettings({ tournament }) {
  const settings = tournament?.settings;
  if (!settings) return null;
  const sceneLabel = GAME_LOCATIONS.find((location) => location.id === settings.sceneLocationId)?.label
    || settings.zone;
  return (
    <div className="mt-4 grid grid-cols-3 gap-2 text-center">
      <div className="border border-white/15 bg-black/40 px-2 py-3">
        <p className="text-[9px] font-black uppercase tracking-wider text-[#C5E6A0]">Velocidad</p>
        <p className="mt-1 font-display text-xl text-white">{Number(settings.speedMultiplier).toFixed(1)}×</p>
      </div>
      <div className="border border-white/15 bg-black/40 px-2 py-3">
        <p className="text-[9px] font-black uppercase tracking-wider text-[#C5E6A0]">Ventana</p>
        <p className="mt-1 font-display text-xl text-white">{Number(settings.toleranceLevel).toFixed(1)}</p>
      </div>
      <div className="border border-white/15 bg-black/40 px-2 py-3">
        <p className="text-[9px] font-black uppercase tracking-wider text-[#C5E6A0]">Locación</p>
        <p className="mt-1 text-[11px] font-black uppercase leading-4 text-white">{sceneLabel}</p>
      </div>
    </div>
  );
}

export function TournamentInviteScreen({
  tournament, error, loading, joining, alreadyPlayed, onJoin, onBack, onRanking,
}) {
  const isClosed = tournament?.status === 'closed';
  return (
    <Panel>
      <FestivalBanner />
      <p className="mt-8 text-[10px] font-black uppercase tracking-[.25em] text-[#F7E04B]">Torneo global</p>
      <h1 className="mt-2 break-words font-display text-4xl uppercase leading-none text-white">
        {tournament ? tournament.name : 'Preparando el torneo'}
      </h1>
      {tournament && (
        <p className="mt-3 text-center text-[10px] font-black uppercase tracking-[.18em] text-[#C5E6A0]">
          {isClosed ? 'Torneo cerrado' : 'Torneo activo · una sola partida por jugador'}
        </p>
      )}
      {tournament && <TournamentSettings tournament={tournament} />}
      <p className="mt-5 text-sm leading-6 text-[#C5E6A0]">
        {isClosed
          ? 'Este torneo ya terminó. Mira el ranking para conocer a la persona ganadora.'
          : 'Todas las personas participantes juegan con la misma velocidad, ventana de golpe, locación y mapa musical. Gana el mayor puntaje; si empatan, decide la precisión.'}
      </p>
      {error && <p role="alert" className="mt-4 text-sm font-bold text-[#F7E04B]">{error}</p>}
      {alreadyPlayed && (
        <p role="status" className="mt-4 border border-[#7EC54A]/60 bg-[#1A4D2E]/40 p-3 text-sm font-bold text-white">
          Ya registraste tu puntaje en este torneo. ¡Suerte en el ranking!
        </p>
      )}
      {!isClosed && !alreadyPlayed && (
        <button
          type="button"
          onClick={onJoin}
          disabled={!tournament || loading || joining}
          className={`${replayButton} mt-7 disabled:opacity-60`}
        >
          {joining ? 'Uniéndote…' : 'Jugar el torneo'}
        </button>
      )}
      <button type="button" onClick={onRanking} className={`${secondary} mt-3`}>Ver ranking del torneo</button>
      <button
        onClick={onBack}
        className="mt-6 text-xs font-bold uppercase tracking-widest text-[#F7E04B] underline underline-offset-4"
      >
        Volver al mapa
      </button>
    </Panel>
  );
}

function QrFigure({ url }) {
  const canvasRef = useRef(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(!renderQrToCanvas(canvasRef.current, url));
  }, [url]);
  if (failed) return null;
  return (
    <figure className="mx-auto mt-4 w-max border-4 border-white bg-white p-1">
      <canvas ref={canvasRef} role="img" aria-label={`Código QR para unirse al torneo: ${url}`} />
    </figure>
  );
}

export function TournamentShareModal({ tournament, url, onClose }) {
  const text = resolveTournamentShareText(tournament);
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(text);
  const [copied, setCopied] = useState('');

  const copyText = async (value, label) => {
    try {
      if (!navigator.clipboard) return;
      await navigator.clipboard.writeText(value);
      setCopied(label);
      window.setTimeout(() => setCopied(''), 2000);
    } catch {
      // El portapapeles puede no estar disponible.
    }
  };

  const openShare = (href) => window.open(href, '_blank', 'noopener,noreferrer');

  const nativeShare = async () => {
    try {
      if (navigator.share) await navigator.share({ title: 'Torneo Ñaca Ñaca', text, url });
      else await copyText(`${text} ${url}`, 'enlace');
    } catch (error) {
      if (error?.name !== 'AbortError') copyText(`${text} ${url}`, 'enlace');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center overflow-y-auto bg-black/80 px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-label="Compartir torneo"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm border border-[#7EC54A]/60 bg-[#0A1A0F] p-6 text-center shadow-[10px_12px_0_rgba(0,0,0,.55)]"
        onClick={(event) => event.stopPropagation()}
      >
        <p className="text-[10px] font-black uppercase tracking-[.25em] text-[#F7E04B]">Comparte el torneo</p>
        <h2 className="mt-2 break-words font-display text-2xl uppercase leading-tight text-white">
          {tournament?.name || 'Torneo global'}
        </h2>
        <QrFigure url={url} />
        <p className="mt-4 break-all border border-[#7EC54A]/60 bg-black/40 p-3 text-left text-xs text-[#F7E04B]">{url}</p>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <button type="button" onClick={() => openShare(`https://wa.me/?text=${encodedText}%20${encodedUrl}`)} className={shareButton}>
            WhatsApp
          </button>
          <button type="button" onClick={() => openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`)} className={shareButton}>
            Facebook
          </button>
          <button type="button" onClick={() => openShare(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`)} className={shareButton}>
            X
          </button>
          <button type="button" onClick={() => copyText(`${text} ${url}`, 'instagram')} className={shareButton}>
            {copied === 'instagram' ? '¡Copiado!' : 'Instagram'}
          </button>
          <button type="button" onClick={() => copyText(url, 'enlace')} className={shareButton}>
            {copied === 'enlace' ? '¡Copiado!' : 'Copiar link'}
          </button>
          <button type="button" onClick={nativeShare} className={shareButton}>Compartir</button>
        </div>
        <p className="mt-3 text-[11px] leading-5 text-[#C5E6A0]">
          Instagram no acepta enlaces directos: copia el texto y pégalo en tu historia o publicación.
        </p>
        <button
          type="button"
          onClick={onClose}
          className="mt-5 text-xs font-bold uppercase tracking-widest text-[#F7E04B] underline underline-offset-4"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}

function rankBadgeClass(rank, isClosed) {
  if (!isClosed) return 'text-white';
  if (rank === 1) return 'text-[#F7E04B]';
  if (rank === 2) return 'text-[#E8E8E8]';
  if (rank === 3) return 'text-[#E8A33D]';
  return 'text-white';
}

export function TournamentRankingSection({ board, isLoading, error }) {
  const tournament = board?.tournament || null;
  const entries = board?.entries || [];
  const isClosed = tournament?.status === 'closed';

  if (isLoading && !board) {
    return <p className="py-10 text-center text-sm text-white">Cargando torneo…</p>;
  }
  if (error && !board) {
    return (
      <p role="status" className="py-10 text-center text-sm text-[#C9A8FF]">
        No se pudo consultar el torneo. Inténtalo de nuevo.
      </p>
    );
  }
  if (!tournament) {
    return <p className="py-10 text-center text-sm text-white">Ahora mismo no hay ningún torneo.</p>;
  }

  return (
    <div className="mt-3 text-left">
      <div className="border border-[#7EC54A]/50 bg-[#1A4D2E]/30 px-4 py-4 text-center">
        <p className="text-[10px] font-black uppercase tracking-[.25em] text-[#F7E04B]">
          {isClosed ? 'Torneo cerrado' : 'Torneo activo'}
        </p>
        <p className="mt-1 break-words font-display text-2xl uppercase leading-tight text-white">{tournament.name}</p>
        <TournamentSettings tournament={tournament} />
      </div>
      <div className="mt-4 flex items-baseline justify-between gap-3 border-b-2 border-[#7C3AED] pb-3">
        <span className="text-base font-normal uppercase tracking-normal text-white">
          {board.total === 1 ? '1 participante' : `${board.total} participantes`}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#C9A8FF]">Mayor puntaje gana</span>
      </div>
      {entries.length ? (
        entries.map((item) => (
          <div
            key={item.username}
            className={[
              'flex items-center gap-3 border-b border-white/20 py-4',
              isClosed && item.rank === 1 ? 'border border-[#F7E04B] bg-[#F7E04B]/10 px-3' : '',
              isClosed && (item.rank === 2 || item.rank === 3) ? 'border border-white/40 bg-white/5 px-3' : '',
            ].join(' ')}
          >
            <span className={`w-12 shrink-0 font-display text-2xl ${rankBadgeClass(item.rank, isClosed)}`}>
              {String(item.rank).padStart(2, '0')}
            </span>
            <span className="flex-1 font-bold text-white">
              {item.username}
              {isClosed && item.rank === 1 && (
                <small className="ml-2 border border-[#F7E04B] bg-[#F7E04B] px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-black">
                  Ganador
                </small>
              )}
              {isClosed && (item.rank === 2 || item.rank === 3) && (
                <small className="ml-2 border border-white/50 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-white">
                  {item.rank === 2 ? 'Segundo' : 'Tercero'}
                </small>
              )}
              <small className="ml-2 block text-[10px] uppercase tracking-wider text-white/70 sm:inline">
                {item.accuracy == null ? 'sin precisión' : `${item.accuracy}% precisión`}
              </small>
            </span>
            <span className="text-right">
              <small className="block text-[9px] font-black uppercase tracking-wider text-white">Puntaje</small>
              <strong className="font-black text-[#F7E04B]">{item.score.toLocaleString()}</strong>
            </span>
          </div>
        ))
      ) : (
        <p className="py-10 text-center text-sm text-white">Aún nadie juega este torneo.</p>
      )}
    </div>
  );
}

